// Driver payout requests + history on the `payouts` collection.
//
// Firestore rules:
//   read:   isDriver() && resource.data.driverId == request.auth.uid
//   create: isDriver() && request.resource.data.driverId == request.auth.uid
//   update: isAdmin()
//
// The iOS AdminPayoutViewModel marks payout records paid/cancelled with
// paymentStatus ("Pending" | "Paid" | "Cancelled"), paidAt, paidBy,
// paymentNotes, paymentReferenceNumber — those fields are read back here for
// the driver's payout history.
import {
  addDoc, collection, onSnapshot, query, serverTimestamp, where, limit,
} from 'firebase/firestore';
import { db } from '../firebase';
import { toDate } from '../models';

export function parsePayout(id, d) {
  return {
    id,
    driverId: d.driverId || '',
    amount: Number(d.amount ?? d.totalEarnings) || 0,
    paymentStatus: d.paymentStatus || 'Pending',
    paymentNotes: d.paymentNotes || '',
    paymentReferenceNumber: d.paymentReferenceNumber || '',
    paidAt: toDate(d.paidAt),
    requestedAt: toDate(d.requestedAt) || toDate(d.createdAt) || new Date(),
    raw: d,
  };
}

// Single-equality listener (no composite index) + client-side sort.
export function listenToPayouts(driverId, cb, onErr) {
  const q = query(
    collection(db, 'payouts'),
    where('driverId', '==', driverId),
    limit(100),
  );
  return onSnapshot(q, (snap) => {
    const list = snap.docs
      .map((d) => parsePayout(d.id, d.data()))
      .sort((a, b) => (b.requestedAt?.getTime() || 0) - (a.requestedAt?.getTime() || 0));
    cb(list);
  }, (e) => { console.error('listenToPayouts', e); onErr && onErr(e); });
}

// Creates a payout request doc. Only `driverId` is rule-checked.
export async function requestPayout(driverId, amount) {
  await addDoc(collection(db, 'payouts'), {
    driverId,
    userType: 'driver',
    amount: Math.round(amount * 100) / 100,
    paymentStatus: 'Pending',
    requestedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}
